import { createSlice } from '@reduxjs/toolkit';
import merge from 'lodash/merge';
import omit from 'lodash/omit';
import pick from 'lodash/pick';
import { normalize } from 'normalizr';
import { FetchStatus } from 'data/core';
import { itemsFetchStatus } from 'data/core/utils';
import { spaceSchema, spaceNormalizer } from './schema';
const INITIAL_DATA = {
    items: {},
    itemsFetchStatus: {},
    loading: false,
    error: null,
    creating: false,
    createError: null,
    isDetailLoading: true,
    currentSpace: null,
    isDetailError: false,
    deleting: false,
    deleteError: null,
    edit: false,
    isEditing: false,
    editError: null,
    isLoading: false,
    isError: false
};
export const spacesSlice = createSlice({
    initialState: INITIAL_DATA,
    name: 'spaces',
    reducers: (create)=>({
            addSpace: create.reducer((state, { payload })=>{
                const data = normalize(payload, spaceSchema);
                return merge({}, {
                    ...state,
                    items: {
                        ...state.items,
                        ...data.entities.spaces
                    }
                });
            }),
            cancelAddSpace: create.reducer((state)=>{
                const items = pick(state.items, Object.keys(state.items).filter((i)=>Number(i) > 0));
                return {
                    ...state,
                    items
                };
            }),
            createStart: create.reducer((state)=>({
                    ...state,
                    creating: true
                })),
            createSuccess: create.reducer((state, { payload })=>{
                const normalized = normalize(payload, spaceSchema);
                const tempState = {
                    ...state
                };
                tempState.items = omit(state.items, 0);
                return merge({}, tempState, {
                    items: {
                        ...normalized.entities.spaces
                    },
                    creating: false
                });
            }),
            saveSpaces: create.reducer((state, { payload })=>{
                const { data, fetchStatus } = payload;
                const normalizedData = normalize(data, [
                    spaceSchema
                ]);
                const { entities: { spaces }, result } = normalizedData;
                if (spaces) {
                    return {
                        ...state,
                        items: spaces,
                        itemsFetchStatus: itemsFetchStatus(result, fetchStatus, state.itemsFetchStatus)
                    };
                }
                return state;
            }),
            setCurrentSpace: create.reducer((state, { payload })=>({
                    ...state,
                    currentSpace: payload
                })),
            fetchDetailStart: create.reducer((state, { payload })=>({
                    ...state,
                    itemsFetchStatus: {
                        ...state.itemsFetchStatus,
                        [payload]: FetchStatus.Loading
                    },
                    isDetailLoading: true
                })),
            fetchDetailSuccess: create.reducer((state, { payload })=>{
                const { entities: { spaces } } = spaceNormalizer(payload);
                return merge({}, state, {
                    items: {
                        ...state.items,
                        ...spaces
                    },
                    itemsFetchStatus: {
                        ...state.itemsFetchStatus,
                        [payload.spaceId]: FetchStatus.Loaded
                    },
                    isDetailLoading: false
                });
            }),
            fetchDetailFailure: create.reducer((state)=>{
                return merge({}, state, {
                    isDetailLoading: false,
                    isDetailError: true
                });
            }),
            deleteStart: create.reducer((state, { payload })=>{
                return {
                    ...state,
                    currentSpace: payload,
                    deleting: true
                };
            }),
            deleteSuccess: create.reducer((state, { payload })=>{
                return {
                    ...state,
                    items: omit(state.items, payload),
                    deleting: false,
                    deleteError: null,
                    currentSpace: null
                };
            }),
            deleteFailure: create.reducer((state, { payload })=>{
                return {
                    ...state,
                    deleting: false,
                    deleteError: payload,
                    currentSpace: null
                };
            }),
            editSpace: create.reducer((state, { payload })=>{
                return {
                    ...state,
                    currentSpace: payload,
                    edit: true,
                    isEditing: false,
                    editError: null
                };
            }),
            cancelEdit: create.reducer((state)=>{
                return {
                    ...state,
                    currentSpace: null,
                    edit: false,
                    isEditing: false,
                    editError: null
                };
            }),
            editStart: create.reducer((state, { payload })=>{
                return {
                    ...state,
                    currentSpace: payload,
                    edit: true,
                    isEditing: true,
                    editError: null
                };
            }),
            editSuccess: create.reducer((state, { payload })=>{
                const normalizedData = normalize(payload, spaceSchema);
                return merge({}, state, {
                    items: {
                        ...state.items,
                        ...normalizedData.entities.spaces
                    },
                    isEditing: false,
                    editError: null,
                    currentSpace: null
                });
            }),
            editFailure: create.reducer((state, { payload })=>{
                return {
                    ...state,
                    edit: false,
                    isEditing: false,
                    editError: payload,
                    currentSpace: null
                };
            }),
            addSpaceTabsToSpace: create.reducer((state, { payload })=>{
                const { spaceId, spaceTabs } = payload;
                const space = state.items[spaceId];
                if (space) {
                    return {
                        ...state,
                        items: {
                            ...state.items,
                            [spaceId]: {
                                ...space,
                                spaceTabs: [
                                    ...space.spaceTabs,
                                    ...spaceTabs
                                ]
                            }
                        }
                    };
                }
                return state;
            }),
            removeSpaceTabFromSpace: create.reducer((state, { payload })=>{
                const { spaceId, spaceTabId } = payload;
                const space = state.items[spaceId];
                if (space) {
                    return {
                        ...state,
                        items: {
                            ...state.items,
                            [spaceId]: {
                                ...space,
                                spaceTabs: [
                                    ...space.spaceTabs.filter((s)=>s !== spaceTabId)
                                ]
                            }
                        }
                    };
                }
                return state;
            })
        })
});
